import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { AddMealSheet } from '../components/AddMealSheet';
import { Card, Chip, Spinner, shortDate, useToast } from '../components/ui';
import type { Language, LibraryMeal } from '../types';

const FILTERS = [
  { id: 'favourites', en: 'Favourites', fr: 'Favoris' },
  { id: 'regulars', en: 'Regulars', fr: 'Habituels' },
  { id: 'recent', en: 'Recently cooked', fr: 'Récents' },
  { id: 'kids', en: "Kids' meals", fr: 'Enfants' },
  { id: 'keto', en: 'Keto', fr: 'Keto' },
  { id: 'fish', en: 'Fish', fr: 'Poisson' },
  { id: 'all', en: 'All meals', fr: 'Tous' },
] as const;

const T = {
  en: { title: 'Meals', search: 'Search meals', none: 'Nothing here yet.', cooked: (n: number) => `Cooked ${n} time${n === 1 ? '' : 's'}`, last: 'Last cooked', regular: 'Regular', makeRegular: 'Mark as one of ours', unRegular: 'Remove from ours', min: 'min',
    favourite: 'Favourite', isFavourite: 'Favourite ✓',
    favouriteTitle: 'Mark as a family favourite — it will come round more often',
    unfavouriteTitle: 'Remove from family favourites',
    addMeal: 'Add a meal',
    ours: 'Ours',
    deleteMeal: 'Delete',
    confirmDelete: (name: string) => `Delete ${name}? It will stop being planned.`,
    deleted: 'Deleted',
    block: 'Never again', blocked: 'Never again ✓',
    blockTitle: 'Stop the planner ever suggesting this',
    unblockTitle: 'Allow this to be planned again',
    blockedNote: 'Blocked — the planner will not suggest this' },
  fr: { title: 'Repas', search: 'Rechercher', none: 'Rien pour le moment.', cooked: (n: number) => `Cuisiné ${n} fois`, last: 'Dernière fois', regular: 'Habituel', makeRegular: 'Ajouter à nos habitudes', unRegular: 'Retirer', min: 'min',
    favourite: 'Favori', isFavourite: 'Favori ✓',
    favouriteTitle: 'Marquer comme favori de la famille — il reviendra plus souvent',
    unfavouriteTitle: 'Retirer des favoris',
    addMeal: 'Ajouter un repas',
    ours: 'À nous',
    deleteMeal: 'Supprimer',
    confirmDelete: (name: string) => `Supprimer ${name} ? Il ne sera plus planifié.`,
    deleted: 'Supprimé',
    block: 'Plus jamais', blocked: 'Plus jamais ✓',
    blockTitle: 'Ne plus jamais proposer ce plat',
    unblockTitle: 'Autoriser à nouveau ce plat',
    blockedNote: 'Bloqué — le planificateur ne le proposera pas' },
} as const;

/** The Meals screen (§48) — favourites, regulars, history and the full library. */
export function MealsPage({ language }: { language: Language }) {
  const t = T[language];
  const toast = useToast();
  const [filter, setFilter] = useState<string>('favourites');
  const [query, setQuery] = useState('');
  const [meals, setMeals] = useState<LibraryMeal[] | null>(null);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setMeals(null);
    setMeals(await api.meals(filter, query));
  }, [filter, query]);

  useEffect(() => {
    const timer = window.setTimeout(() => void load(), query ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, query]);

  const toggleBlocked = async (meal: LibraryMeal) => {
    await api.block(meal.id, !meal.blocked);
    setMeals(
      (current) =>
        current?.map((m) => (m.id === meal.id ? { ...m, blocked: !m.blocked } : m)) ?? null,
    );
  };

  const toggleFavourite = async (meal: LibraryMeal) => {
    await api.favourite(meal.id, !meal.favourite);
    setMeals((current) =>
      current?.map((m) => (m.id === meal.id ? { ...m, favourite: !m.favourite } : m)) ?? null,
    );
  };

  const toggleRegular = async (meal: LibraryMeal) => {
    await api.setRegular(meal.id, !meal.is_regular);
    toast(meal.is_regular ? t.unRegular : t.makeRegular);
    setMeals((current) =>
      current?.map((m) => (m.id === meal.id ? { ...m, is_regular: !m.is_regular } : m)) ?? null,
    );
  };

  return (
    <div className="stack">
      {adding && (
        <AddMealSheet
          language={language}
          onSaved={() => void load()}
          onClose={() => setAdding(false)}
        />
      )}
      <div className="row">
        <h1 style={{ marginRight: 'auto' }}>{t.title}</h1>
        <button className="btn primary" onClick={() => setAdding(true)}>
          + {t.addMeal}
        </button>
      </div>

      <div className="row" style={{ gap: 6 }}>
        {FILTERS.map((item) => (
          <button
            key={item.id}
            className={`btn small${filter === item.id ? ' on' : ''}`}
            onClick={() => setFilter(item.id)}
          >
            {language === 'fr' ? item.fr : item.en}
          </button>
        ))}
      </div>

      <input
        type="text"
        value={query}
        placeholder={t.search}
        onChange={(event) => setQuery(event.target.value)}
      />

      {!meals ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 40 }}>
          <Spinner />
        </div>
      ) : meals.length === 0 ? (
        <Card>
          <p className="dim">{t.none}</p>
        </Card>
      ) : (
        <Card tight>
          <div style={{ margin: '0 -18px' }}>
            {meals.map((meal) => (
              <div className="dish" key={meal.id}>
                <div className="dish-top">
                  <div className="dish-main">
                    <h3>{meal.name}</h3>
                    {meal.description && <p className="desc">{meal.description}</p>}
                    <div className="chips">
                      <Chip>{meal.meal_class}</Chip>
                      <Chip>{meal.prep_type === 'quick' ? 'Quick' : 'Regular'}</Chip>
                      <Chip>
                        {meal.prep_minutes + meal.cook_minutes} {t.min}
                      </Chip>
                      {meal.is_fish_meal && <Chip kind="fish">Fish</Chip>}
                      {meal.keto_compatible && <Chip kind="keto">Keto</Chip>}
                      {meal.is_regular && <Chip kind="accent">{t.regular}</Chip>}
                      {meal.blocked && <Chip kind="fav">{t.blockedNote}</Chip>}
                    </div>

                    {meal.times_cooked > 0 && (
                      <p className="dim" style={{ marginBottom: 10 }}>
                        {t.cooked(meal.times_cooked)}
                        {meal.last_cooked ? ` · ${t.last} ${shortDate(meal.last_cooked, language)}` : ''}
                      </p>
                    )}

                    <div className="actions">
                      <button
                        className={`btn${meal.favourite ? ' on' : ''}`}
                        aria-pressed={meal.favourite}
                        title={meal.favourite ? t.unfavouriteTitle : t.favouriteTitle}
                        onClick={() => void toggleFavourite(meal)}
                      >
                        ♥ {meal.favourite ? t.isFavourite : t.favourite}
                      </button>
                      <button className={`btn small${meal.is_regular ? ' on' : ''}`} onClick={() => void toggleRegular(meal)}>
                        {meal.is_regular ? t.unRegular : t.makeRegular}
                      </button>
                      {/* The other end of the same judgement as ♥. */}
                      <button
                        className={`btn small${meal.blocked ? ' on' : ''}`}
                        aria-pressed={meal.blocked}
                        title={meal.blocked ? t.unblockTitle : t.blockTitle}
                        onClick={() => void toggleBlocked(meal)}
                      >
                        {meal.blocked ? t.blocked : t.block}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
